import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import StarRounded from "@mui/icons-material/StarRounded";
import { useCookies } from "react-cookie";
import { CARD_STYLES, FONTS } from "../../../constants/styles";
import ClientAnalyticsApi, {
    ClientAnalyticsData,
} from "../../../api/ClientAnalytics";
import { PeriodSelection } from "./types";
import { SECTION_LABEL_SX, StatementRow, SortPill } from "./analyticsUi";

interface Props {
    selection: PeriodSelection;
}

type SortMode = "revenue" | "frequency" | "avgValue";

// Each client gets their own soft colour — like a real address book. A
// deliberate exception to the brand-only palette rule: the Clients section is
// about people, and varied hues give it life the money screens don't need.
const AVATAR_COLORS: { bg: string; fg: string }[] = [
    { bg: "#FDE7EA", fg: "#D6455F" }, // rose
    { bg: "#FCEBD9", fg: "#C77A26" }, // amber
    { bg: "#E8F3E4", fg: "#5B8C4E" }, // sage
    { bg: "#E3EEF9", fg: "#3F72A8" }, // sky
    { bg: "#EFE7F7", fg: "#8155B5" }, // lilac
    { bg: "#E6F1F0", fg: "#3E8E88" }, // teal
    { bg: "#FBE9E3", fg: "#C15F3C" }, // terracotta
];

const avatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++)
        hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const initialOf = (name: string) =>
    (name?.trim()?.[0] ?? "?").toUpperCase();

const ClientAvatar = ({ name, size }: { name: string; size: number }) => {
    const { bg, fg } = avatarColor(name);
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                bgcolor: bg,
                color: fg,
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: size * 0.42,
                lineHeight: 1,
            }}
        >
            {initialOf(name)}
        </Box>
    );
};

const ClientAnalytics: React.FC<Props> = ({ selection }) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [data, setData] = useState<ClientAnalyticsData | undefined>();
    const [loading, setLoading] = useState(true);
    const [sortMode, setSortMode] = useState<SortMode>("revenue");
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!uuid || !brand_id || !location_id) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        ClientAnalyticsApi.info(uuid, brand_id, location_id, selection)
            .then((res) => {
                if (requestId !== requestIdRef.current) return; // stale
                setData(res);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoading(false);
            });
        // `selection` is memoized in the parent (stable per period/from/to).
    }, [uuid, brand_id, location_id, selection]);

    const sortedTop = useMemo(() => {
        if (!data) return [];
        const copy = [...data.topClients];
        copy.sort((a, b) =>
            sortMode === "revenue"
                ? b.revenue - a.revenue
                : sortMode === "frequency"
                ? b.visits - a.visits
                : b.avgValue - a.avgValue
        );
        return copy;
    }, [data, sortMode]);

    if (loading && !data) {
        return (
            <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
            </Stack>
        );
    }
    if (!data) {
        return (
            <Stack sx={CARD_STYLES} spacing={1}>
                <Typography fontSize="14px" color="text.secondary">
                    No client analytics yet — this fills in as clients book.
                </Typography>
            </Stack>
        );
    }

    const { overview, health } = data;
    const newDelta = overview.newThisPeriod - overview.newLastPeriod;
    const newValue = `${overview.newThisPeriod}${
        newDelta !== 0 ? ` (${newDelta > 0 ? "+" : ""}${newDelta})` : ""
    }`;
    const featured = sortedTop[0];
    const rest = sortedTop.slice(1);

    return (
        <Stack className="force-dynamic-translation" spacing={2} width="100%">
            {/* ── Overview — hero on active clients (a count, so DM Sans) ── */}
            <Stack sx={CARD_STYLES} spacing={1.25}>
                <Typography sx={SECTION_LABEL_SX}>Overview</Typography>
                <Stack direction="row" alignItems="baseline" spacing={0.9}>
                    <Typography
                        className="no-translation"
                        fontFamily={FONTS.display}
                        fontWeight={700}
                        fontSize="40px"
                        color={primary}
                        lineHeight={1}
                    >
                        {overview.activeCount}
                    </Typography>
                    <Typography fontSize="13px" color="text.secondary">
                        active clients · last 90 days
                    </Typography>
                </Stack>
                <Stack spacing={0} pt={0.5}>
                    <StatementRow
                        label="Total clients"
                        value={`${overview.totalClients}`}
                    />
                    <StatementRow label="New this period" value={newValue} />
                    <StatementRow
                        label="Returning rate"
                        value={`${Math.round(overview.returningRatePct)}%`}
                    />
                    <StatementRow
                        label="Average visits per client"
                        value={overview.avgVisitsPerClient.toFixed(1)}
                    />
                </Stack>
            </Stack>

            {/* ── Top clients — faces, and a celebrated #1 ── */}
            <Stack sx={CARD_STYLES} spacing={1.5}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.75}
                >
                    <Typography sx={SECTION_LABEL_SX}>Top clients</Typography>
                    <Stack direction="row" spacing={0.75}>
                        <SortPill
                            label="€"
                            active={sortMode === "revenue"}
                            onClick={() => setSortMode("revenue")}
                            color={primary}
                        />
                        <SortPill
                            label="visits"
                            active={sortMode === "frequency"}
                            onClick={() => setSortMode("frequency")}
                            color={primary}
                        />
                        <SortPill
                            label="avg"
                            active={sortMode === "avgValue"}
                            onClick={() => setSortMode("avgValue")}
                            color={primary}
                        />
                    </Stack>
                </Stack>

                {!featured ? (
                    <Typography fontSize="13px" color="text.secondary">
                        No clients with completed visits yet.
                    </Typography>
                ) : (
                    <>
                        {/* Featured #1 — the master's most valued regular */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                position: "relative",
                                borderRadius: "14px",
                                p: 1.5,
                                background: `linear-gradient(135deg, ${alpha(
                                    primary,
                                    0.1
                                )} 0%, ${alpha(primary, 0.03)} 100%)`,
                            }}
                        >
                            <Box sx={{ position: "relative", flex: "0 0 auto" }}>
                                <ClientAvatar name={featured.name} size={52} />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: -4,
                                        right: -4,
                                        width: 22,
                                        height: 22,
                                        borderRadius: "50%",
                                        bgcolor: primary,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow:
                                            "0 2px 6px rgba(0,0,0,0.2)",
                                    }}
                                >
                                    <StarRounded
                                        sx={{ fontSize: 14, color: "white" }}
                                    />
                                </Box>
                            </Box>
                            <Stack spacing={0.25} minWidth={0}>
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontWeight={700}
                                    fontSize="17px"
                                    noWrap
                                >
                                    {featured.name}
                                </Typography>
                                <Typography
                                    className="no-translation"
                                    fontSize="13px"
                                    color="text.secondary"
                                >
                                    €{Math.round(featured.revenue)} · ×
                                    {featured.visits} · avg €
                                    {Math.round(featured.avgValue)}
                                </Typography>
                            </Stack>
                        </Stack>

                        {/* The rest, with faces */}
                        {rest.length > 0 && (
                            <Stack spacing={1.25} pt={0.25}>
                                {rest.map((c, i) => (
                                    <Stack
                                        key={`${c.name}-${i}`}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.25}
                                    >
                                        <ClientAvatar name={c.name} size={32} />
                                        <Typography
                                            fontSize="14px"
                                            fontWeight={500}
                                            flex={1}
                                            minWidth={0}
                                            noWrap
                                        >
                                            {c.name}
                                        </Typography>
                                        <Typography
                                            className="no-translation"
                                            fontFamily={FONTS.display}
                                            fontWeight={700}
                                            fontSize="14px"
                                            color={primary}
                                            sx={{ flex: "0 0 auto" }}
                                        >
                                            €{Math.round(c.revenue)}
                                        </Typography>
                                        <Typography
                                            className="no-translation"
                                            fontSize="12px"
                                            color="text.secondary"
                                            sx={{
                                                minWidth: 30,
                                                textAlign: "right",
                                                flex: "0 0 auto",
                                            }}
                                        >
                                            ×{c.visits}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </>
                )}
            </Stack>

            {/* ── Client health — colour-coded pulse ── */}
            <Stack sx={CARD_STYLES} spacing={1.25}>
                <Typography sx={SECTION_LABEL_SX}>Client health</Typography>
                <Stack spacing={0}>
                    <HealthRow
                        color={theme.palette.warning.main}
                        label="At risk of churning (45–60 days)"
                        value={`${health.churnRiskCount}`}
                    />
                    <HealthRow
                        color={theme.palette.text.disabled}
                        label="Lost (90+ days)"
                        value={`${health.lostCount}`}
                    />
                    <HealthRow
                        color={primary}
                        label="One-time visitors"
                        value={`${health.oneTimerCount}`}
                    />
                </Stack>
                {health.churnRisk.length > 0 && (
                    <Box pt={0.25}>
                        <Typography
                            fontSize="12px"
                            color="text.secondary"
                            mb={0.75}
                        >
                            Reach out before they drift:
                        </Typography>
                        <Stack spacing={0.75}>
                            {health.churnRisk.map((c, i) => (
                                <Stack
                                    key={`${c.name}-${i}`}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <ClientAvatar name={c.name} size={28} />
                                    <Typography
                                        fontSize="13px"
                                        flex={1}
                                        minWidth={0}
                                        noWrap
                                    >
                                        {c.name}
                                    </Typography>
                                    <Typography
                                        className="no-translation"
                                        fontSize="12px"
                                        color={theme.palette.warning.main}
                                        fontWeight={600}
                                        sx={{ flex: "0 0 auto" }}
                                    >
                                        {c.daysSince}d
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
};

// A statement row with a leading colour dot — gives the health section a pulse
// without a cramped multi-column layout (stays RU-safe).
const HealthRow = ({
    color,
    label,
    value,
}: {
    color: string;
    label: string;
    value: string;
}) => (
    <Stack
        direction="row"
        alignItems="baseline"
        spacing={1.25}
        sx={{
            py: 0.9,
            borderBottom: "1px solid rgba(0,0,0,0.05)",
            "&:last-of-type": { borderBottom: "none", pb: 0 },
        }}
    >
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: color,
                flex: "0 0 auto",
                transform: "translateY(-1px)",
            }}
        />
        <Typography
            fontSize="13px"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 0 }}
        >
            {label}
        </Typography>
        <Typography
            className="no-translation"
            fontFamily={FONTS.display}
            fontWeight={700}
            fontSize="16px"
            noWrap
            sx={{ flex: "0 0 auto" }}
        >
            {value}
        </Typography>
    </Stack>
);

export default ClientAnalytics;
